import audienceDesktop from "@/assets/uploads/audience-desktop.webp.asset.json";
import audienceMobile from "@/assets/uploads/audience-mobile.webp.asset.json";
import drinksDesktop from "@/assets/uploads/drinks-desktop.webp.asset.json";
import drinksMobile from "@/assets/uploads/drinks-mobile.webp.asset.json";
import screenDesktop from "@/assets/uploads/screen-desktop.webp.asset.json";
import screenMobile from "@/assets/uploads/screen-mobile.webp.asset.json";
import horecaDesktop from "@/assets/uploads/horeca-desktop.webp.asset.json";
import horecaMobile from "@/assets/uploads/horeca-mobile.webp.asset.json";
import coupleDesktop from "@/assets/uploads/couple-desktop.webp.asset.json";
import coupleMobile from "@/assets/uploads/couple-mobile.webp.asset.json";
import { useI18n } from "@/i18n/I18nProvider";
import { useMediaSlot } from "./PageMediaProvider";

const audience = { desktop: audienceDesktop.url, mobile: audienceMobile.url };
const drinks   = { desktop: drinksDesktop.url,   mobile: drinksMobile.url };
const screen   = { desktop: screenDesktop.url,   mobile: screenMobile.url };
const horeca   = { desktop: horecaDesktop.url,   mobile: horecaMobile.url };
const couple   = { desktop: coupleDesktop.url,   mobile: coupleMobile.url };

export function Gallery() {
  const { t } = useI18n();
  const mScreen = useMediaSlot("gallery_screen", screen);
  const mAudience = useMediaSlot("gallery_audience", audience);
  const mHoreca = useMediaSlot("gallery_horeca", horeca);
  const mCouple = useMediaSlot("gallery_couple", couple);
  const mDrinks = useMediaSlot("gallery_drinks", drinks);
  return (
    <section id="sfeer" className="relative py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="grid items-end gap-8 md:grid-cols-12">
          <div className="md:col-span-7">
            <p className="font-mono-cinema text-[10px] text-gold">{t.gallery.eyebrow}</p>
            <h2 className="mt-5 font-display text-4xl font-medium leading-[1.05] md:text-5xl lg:text-6xl">
              {t.gallery.title1}
              <span className="block italic text-gold">{t.gallery.title2}</span>
            </h2>
          </div>
          <p className="max-w-md text-foreground/65 md:col-span-5">
            {t.gallery.body}
          </p>
        </div>

        <div className="mt-14 grid grid-cols-12 gap-3 md:gap-4">
          <Figure className="col-span-12 aspect-[16/9]" src={mScreen} alt={mScreen.alt || t.gallery.alts.screen} caption={t.gallery.captions.screen} credit={mScreen.credit} />
          <Figure className="col-span-6 md:col-span-3 aspect-[4/5]" src={mAudience} alt={mAudience.alt || t.gallery.alts.audience} caption={t.gallery.captions.audience} credit={mAudience.credit} />
          <Figure className="col-span-6 md:col-span-3 aspect-[4/5]" src={mHoreca} alt={mHoreca.alt || t.gallery.alts.horeca} caption={t.gallery.captions.horeca} credit={mHoreca.credit} />
          <Figure className="col-span-6 md:col-span-3 aspect-[4/5]" src={mCouple} alt={mCouple.alt || t.gallery.alts.couple} caption={t.gallery.captions.couple} credit={mCouple.credit} />
          <Figure className="col-span-6 md:col-span-3 aspect-[4/5]" src={mDrinks} alt={mDrinks.alt || t.gallery.alts.drinks} caption={t.gallery.captions.drinks} credit={mDrinks.credit} />
        </div>
      </div>
    </section>
  );
}

function Figure({
  src,
  alt,
  caption,
  credit,
  className = "",
  tall = false,
}: {
  src: { desktop: string; mobile: string };
  alt: string;
  caption: string;
  credit?: string | null;
  className?: string;
  tall?: boolean;
}) {
  return (
    <figure
      className={`group relative overflow-hidden rounded-2xl border border-border bg-card ${className}`}
    >
      <picture className="block h-full w-full">
        <source media="(max-width: 767px)" srcSet={src.mobile} />
        <source media="(min-width: 768px)" srcSet={src.desktop} />
        <img
          src={src.desktop}
          width={1200}
          height={1500}
          loading="lazy"
          decoding="async"
          alt={alt}
          className={`h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04] ${tall ? "" : ""}`}
        />
      </picture>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-night-deep/85 via-night-deep/10 to-transparent opacity-90" />
      <figcaption className="absolute inset-x-0 bottom-0 p-5">
        <span className="font-mono-cinema text-[10px] text-gold">●</span>
        <span className="ml-2 font-display text-sm text-foreground/90 md:text-base">{caption}</span>
        {credit && (
          <span className="mt-1 block font-mono-cinema text-[10px] uppercase tracking-wider text-foreground/60">
            Foto: {credit}
          </span>
        )}
      </figcaption>
    </figure>
  );
}
